import React from 'react';
import { Heart, Quotes, MusicNotes, Compass, ForkKnife, Airplane, BookOpen, FilmStrip } from '@phosphor-icons/react';

/**
 * Immersive, pure SVG-and-CSS based Resonance Orb.
 *
 * @param {object} props - Presentation configuration.
 * @param {number} [props.size=180] - Diameter of the orb container in pixels.
 * @param {string} [props.color] - Custom CSS color value (defaults to Milan accent).
 * @returns {React.ReactElement} The animated Resonance Orb.
 */
export default function ResonanceOrb({ size = 240, color, iconType = 'heart', scale = 1, tasteSelections }) {
  const orbColor = color || 'hsl(var(--milan-accent))';

  const getScale = (domainName) => {
    if (!tasteSelections) return 1;
    const intensity = tasteSelections[domainName];
    if (intensity === 'Casual') return 0.65;
    if (intensity === 'Regular') return 1.0;
    if (intensity === 'Core') return 1.5;
    return 1;
  };

  const isTaste = iconType === 'taste';

  // Core icon sizing
  const coreSize = 24 * scale;
  const coreX = 140 - coreSize / 2;
  const coreY = 140 - coreSize / 2;

  // Orbital icon sizing
  const sMusic = 24 * getScale('Music');
  const sFood = 24 * getScale('Food & Dining');
  const sTravel = 24 * getScale('Travel');
  const sBooks = 24 * getScale('Books');
  const sMovies = 24 * getScale('Movies & Shows');

  return (
    <div
      className="resonance-orb"
      style={{
        width: size,
        height: size,
        '--orb-color': orbColor,
      }}
    >
      <svg
        className="resonance-orb-svg"
        viewBox="0 0 280 280"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="vortexGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--orb-color)" stopOpacity="0.15" />
            <stop offset="50%" stopColor="var(--orb-color)" stopOpacity="0.05" />
            <stop offset="100%" stopColor="var(--orb-color)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* 1. Ambient Rotating Light Field */}
        <g className="orb-vortex">
          <circle cx="105" cy="165" r="126" fill="url(#vortexGradient)" />
          <circle cx="185" cy="125" r="112" fill="url(#vortexGradient)" />
          <circle cx="125" cy="85" r="98" fill="url(#vortexGradient)" />
        </g>

        {/* 2. Concentric Pulse Waves */}
        <circle className="orb-pulse orb-pulse-1" cx="140" cy="140" r="130" stroke="var(--orb-color)" strokeWidth="1.5" fill="none" />
        <circle className="orb-pulse orb-pulse-2" cx="140" cy="140" r="130" stroke="var(--orb-color)" strokeWidth="1.5" fill="none" />
        <circle className="orb-pulse orb-pulse-3" cx="140" cy="140" r="130" stroke="var(--orb-color)" strokeWidth="1.5" fill="none" />

        {/* 3. Dashed Technical Ring */}
        <g className="orb-aura">
          <circle cx="140" cy="140" r="50" stroke="var(--orb-color)" strokeWidth="1.5" strokeDasharray="4 6" strokeOpacity="0.4" fill="none" />
          <circle cx="140" cy="140" r="44" stroke="var(--orb-color)" strokeWidth="1" strokeOpacity="0.15" fill="none" />
        </g>

        {/* 4. Orbital Particles */}
        <g className="orb-orbit orb-orbit-1">
          <circle cx="140" cy="140" r="75" stroke="var(--orb-color)" strokeWidth="0.5" strokeOpacity="0.1" fill="none" />
          {isTaste ? (
            <>
              <MusicNotes x={215 - sMusic / 2} y={140 - sMusic / 2} width={sMusic} height={sMusic} color="var(--orb-color)" weight="regular" />
              <ForkKnife x={65 - sFood / 2} y={140 - sFood / 2} width={sFood} height={sFood} color="var(--orb-color)" weight="regular" />
            </>
          ) : (
            <>
              <circle cx="215" cy="140" r="2.5" fill="var(--orb-color)" opacity="0.8" />
              <circle cx="65" cy="140" r="2.5" fill="var(--orb-color)" opacity="0.8" />
            </>
          )}
        </g>
        <g className="orb-orbit orb-orbit-2">
          <circle cx="140" cy="140" r="105" stroke="var(--orb-color)" strokeWidth="0.5" strokeOpacity="0.1" fill="none" />
          {isTaste ? (
            <>
              <Airplane x={140 - sTravel / 2} y={245 - sTravel / 2} width={sTravel} height={sTravel} color="var(--orb-color)" weight="regular" />
              <BookOpen x={140 - sBooks / 2} y={35 - sBooks / 2} width={sBooks} height={sBooks} color="var(--orb-color)" weight="regular" />
            </>
          ) : (
            <>
              <circle cx="140" cy="245" r="2" fill="var(--orb-color)" opacity="0.6" />
              <circle cx="231" cy="92" r="2" fill="var(--orb-color)" opacity="0.6" />
              <circle cx="49" cy="92" r="2" fill="var(--orb-color)" opacity="0.6" />
            </>
          )}
        </g>
        <g className="orb-orbit orb-orbit-3">
          <circle cx="140" cy="140" r="125" stroke="var(--orb-color)" strokeWidth="0.5" strokeOpacity="0.1" fill="none" />
          {isTaste ? (
            <FilmStrip x={140 - sMovies / 2} y={15 - sMovies / 2} width={sMovies} height={sMovies} color="var(--orb-color)" weight="regular" />
          ) : (
            <circle cx="140" cy="15" r="3" fill="var(--orb-color)" opacity="0.7" />
          )}
        </g>

        {/* 5. Breathing Core Anchor */}
        <g className="orb-core">
          <circle cx="140" cy="140" r="24" fill="var(--orb-color)" opacity="0.15" />
          <circle cx="140" cy="140" r="20" stroke="var(--orb-color)" strokeWidth="0.5" strokeOpacity="0.2" fill="none" />
          {iconType === 'heart' && <Heart x={coreX} y={coreY} width={coreSize} height={coreSize} color="var(--orb-color)" weight="regular" />}
          {iconType === 'quote' && <Quotes x={coreX} y={coreY} width={coreSize} height={coreSize} color="var(--orb-color)" weight="regular" />}
          {iconType === 'music' && <MusicNotes x={coreX} y={coreY} width={coreSize} height={coreSize} color="var(--orb-color)" weight="regular" />}
          {iconType === 'compass' && <Compass x={coreX} y={coreY} width={coreSize} height={coreSize} color="var(--orb-color)" weight="regular" />}
        </g>
      </svg>
    </div>
  );
}
